import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Instagram as InstagramIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { format, parse, startOfWeek, getDay, isToday, isBefore, startOfDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { leadApi } from '../services/api';
import { LoadingSpinner } from '../components/common';
import StageChip from '../components/common/StageChip';
import NicheChip from '../components/common/NicheChip';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const FollowupCalendar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [followupEvents, setFollowupEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchFollowups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await leadApi.getCalendarFollowups();
      setFollowupEvents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch followups:', error);
      // Fallback to old method if new endpoint fails
      try {
        const leadsResponse = await leadApi.getAll({ limit: 1000 });
        const leads = leadsResponse.data.leads || [];
        const events = leads
          .filter((lead) => lead.nextFollowupDate)
          .map((lead) => ({
            id: lead._id,
            leadId: lead._id,
            title: lead.businessName || lead.instagramHandle,
            date: lead.nextFollowupDate,
            notes: lead.followupNotes,
            type: 'manual',
            lead: {
              instagramHandle: lead.instagramHandle,
              businessName: lead.businessName,
              niche: lead.niche,
              stage: lead.stage,
              location: lead.location || [lead.city, lead.stateProvince].filter(Boolean).join(', ')
            }
          }));
        setFollowupEvents(events);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFollowups();
  }, [fetchFollowups]);

  const getEventColor = (date, type) => {
    const today = startOfDay(new Date());
    const eventDate = startOfDay(new Date(date));

    if (isBefore(eventDate, today)) {
      return { bg: theme.palette.error.main, border: theme.palette.error.dark };
    }
    if (isToday(eventDate)) {
      return { bg: theme.palette.warning.main, border: theme.palette.warning.dark };
    }
    // Different color for auto-scheduled vs manual
    if (type === 'auto_stage') {
      return { bg: theme.palette.info.main, border: theme.palette.info.dark };
    }
    return { bg: theme.palette.primary.main, border: theme.palette.primary.dark };
  };

  const events = useMemo(() => {
    return followupEvents.map((followup) => {
      const colors = getEventColor(followup.date, followup.type);
      return {
        id: followup.id,
        leadId: followup.leadId,
        title: followup.title || followup.lead?.businessName || followup.lead?.instagramHandle,
        start: new Date(followup.date),
        end: new Date(followup.date),
        allDay: true,
        resource: {
          ...followup.lead,
          nextFollowupDate: followup.date,
          followupNotes: followup.notes,
          followupType: followup.type
        },
        colors,
      };
    });
  }, [followupEvents, theme]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleNavigateToLead = () => {
    if (selectedEvent) {
      navigate(`/leads/${selectedEvent.leadId || selectedEvent.id}`);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.colors.bg,
        borderColor: event.colors.border,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '0.75rem',
        padding: '2px 4px',
      },
    };
  };

  const CustomToolbar = ({ label, onNavigate, onView, view }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => onNavigate('PREV')}>
          <ChevronLeft />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          startIcon={<TodayIcon />}
          onClick={() => onNavigate('TODAY')}
        >
          Today
        </Button>
        <IconButton size="small" onClick={() => onNavigate('NEXT')}>
          <ChevronRight />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            size="small"
            sx={{ bgcolor: theme.palette.error.main, color: 'white', height: 20, fontSize: '0.7rem' }}
            label="Overdue"
          />
          <Chip
            size="small"
            sx={{ bgcolor: theme.palette.warning.main, color: 'white', height: 20, fontSize: '0.7rem' }}
            label="Today"
          />
          <Chip
            size="small"
            sx={{ bgcolor: theme.palette.primary.main, color: 'white', height: 20, fontSize: '0.7rem' }}
            label="Manual"
          />
          <Chip
            size="small"
            sx={{ bgcolor: theme.palette.info.main, color: 'white', height: 20, fontSize: '0.7rem' }}
            label="Auto"
          />
        </Box>

        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => onView('month')}
            variant={view === 'month' ? 'contained' : 'outlined'}
          >
            Month
          </Button>
          <Button
            onClick={() => onView('week')}
            variant={view === 'week' ? 'contained' : 'outlined'}
          >
            Week
          </Button>
          <Button
            onClick={() => onView('agenda')}
            variant={view === 'agenda' ? 'contained' : 'outlined'}
          >
            Agenda
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );

  if (loading) {
    return <LoadingSpinner message="Loading calendar..." />;
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Follow-up Calendar
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {events.length} scheduled follow-ups
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/leads')}
          >
            View All Leads
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            height: 'calc(100vh - 220px)',
            minHeight: 500,
            '& .rbc-calendar': {
              fontFamily: theme.typography.fontFamily,
            },
            '& .rbc-header': {
              padding: '8px 4px',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .rbc-month-view': {
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
            },
            '& .rbc-day-bg': {
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            },
            '& .rbc-today': {
              backgroundColor: theme.palette.primary.light + '20',
            },
            '& .rbc-off-range-bg': {
              backgroundColor: theme.palette.grey[50],
            },
            '& .rbc-event': {
              cursor: 'pointer',
            },
            '& .rbc-event:focus': {
              outline: 'none',
            },
            '& .rbc-toolbar': {
              display: 'none',
            },
            '& .rbc-month-row': {
              minHeight: 100,
            },
            '& .rbc-date-cell': {
              padding: '4px 8px',
              fontSize: '0.8rem',
              fontWeight: 500,
            },
            '& .rbc-agenda-view': {
              '& table': {
                borderCollapse: 'collapse',
                '& th, & td': {
                  padding: '8px 12px',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& th': {
                  backgroundColor: theme.palette.grey[50],
                  fontWeight: 600,
                },
              },
            },
            '& .rbc-agenda-date-cell, & .rbc-agenda-time-cell': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={['month', 'week', 'agenda']}
            view={view}
            date={currentDate}
            onView={setView}
            onNavigate={setCurrentDate}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            components={{
              toolbar: CustomToolbar,
            }}
            popup
            selectable={false}
          />
        </Box>
      </Paper>

      {/* Lead Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Follow-up Details
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InstagramIcon color="action" fontSize="small" />
                <Typography variant="body1" fontWeight={600}>
                  @{selectedEvent.resource.instagramHandle}
                </Typography>
              </Box>

              {selectedEvent.resource.businessName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {selectedEvent.resource.businessName}
                  </Typography>
                </Box>
              )}

              {selectedEvent.resource.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocationIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {selectedEvent.resource.location}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 2 }}>
                <StageChip stage={selectedEvent.resource.stage} />
                {selectedEvent.resource.niche && (
                  <NicheChip niche={selectedEvent.resource.niche} />
                )}
              </Box>

              <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Follow-up Date
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {format(new Date(selectedEvent.resource.nextFollowupDate), 'EEEE, MMMM d, yyyy')}
                </Typography>
                {selectedEvent.resource.followupNotes && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {selectedEvent.resource.followupNotes}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Tooltip title="Open lead details">
            <Button
              variant="contained"
              endIcon={<OpenInNewIcon />}
              onClick={handleNavigateToLead}
            >
              View Lead
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FollowupCalendar;
